import React, { useEffect, useMemo, useState } from 'react';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { Modal, ModalActions, ModalContent, ModalTitle } from '../../components/basic';
import ConfirmDialog from '../../components/modals/ConfirmDialog';

interface AdminClient {
  _id: string;
  name: string;
  city?: string;
  state?: string;
}

interface SeasonOption {
  _id: string;
  name: string;
  startDate?: string;
  endDate?: string;
  active?: boolean;
}

interface LeaderboardEntry {
  clientId: string;
  seasonId: string;
  clientName: string;
  seasonName: string;
}

interface LeaderboardRow {
  shareId: string;
  shareUrl: string;
  name: string;
  entries: LeaderboardEntry[];
  createdAt?: string;
  accessCount?: number;
}

interface LeaderboardModalProps {
  isOpen: boolean;
  clients: AdminClient[];
  leaderboard?: LeaderboardRow | null;
  onClose: () => void;
  onSaved: () => void;
}

const LeaderboardModal: React.FC<LeaderboardModalProps> = ({ isOpen, clients, leaderboard, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [selectedSeasonByClient, setSelectedSeasonByClient] = useState<Record<string, string>>({});
  const [seasonOptionsByClient, setSeasonOptionsByClient] = useState<Record<string, SeasonOption[]>>({});
  const [loadingSeasonByClient, setLoadingSeasonByClient] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(leaderboard?.name || '');
    setSearchTerm('');
    const initialEntries = leaderboard?.entries || [];
    setSelectedClientIds(initialEntries.map((entry) => entry.clientId));
    setSelectedSeasonByClient(
      initialEntries.reduce(
        (acc, entry) => {
          acc[entry.clientId] = entry.seasonId;
          return acc;
        },
        {} as Record<string, string>,
      ),
    );
    setSeasonOptionsByClient({});
    setLoadingSeasonByClient({});
    setSaving(false);
  }, [isOpen, leaderboard]);

  useEffect(() => {
    if (!isOpen || !selectedClientIds.length) return;

    selectedClientIds.forEach((clientId) => {
      if (seasonOptionsByClient[clientId] || loadingSeasonByClient[clientId]) return;

      const loadSeasons = async () => {
        setLoadingSeasonByClient((prev) => ({ ...prev, [clientId]: true }));
        try {
          const response = await api.get(`${API_URLS.ADMIN_CLIENT_SEASONS}/${clientId}/seasons`);
          if (!response.data.success) {
            showErrorMsg(response.data.message || 'Failed to load seasons');
            setSeasonOptionsByClient((prev) => ({ ...prev, [clientId]: [] }));
            return;
          }

          setSeasonOptionsByClient((prev) => ({ ...prev, [clientId]: response.data.data || [] }));
        } catch (error: any) {
          showErrorMsg(error.response?.data?.message || 'Failed to load seasons');
          setSeasonOptionsByClient((prev) => ({ ...prev, [clientId]: [] }));
        } finally {
          setLoadingSeasonByClient((prev) => ({ ...prev, [clientId]: false }));
        }
      };

      loadSeasons();
    });
  }, [isOpen, loadingSeasonByClient, seasonOptionsByClient, selectedClientIds]);

  const filteredClients = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return clients;

    return clients.filter((client) =>
      [client.name, client.city, client.state].some((value) => (value || '').toLowerCase().includes(query)),
    );
  }, [clients, searchTerm]);

  const selectedClientSet = useMemo(() => new Set(selectedClientIds), [selectedClientIds]);

  const toggleClient = (clientId: string) => {
    setSelectedClientIds((prev) => {
      if (prev.includes(clientId)) {
        setSelectedSeasonByClient((seasonMap) => {
          const next = { ...seasonMap };
          delete next[clientId];
          return next;
        });
        return prev.filter((id) => id !== clientId);
      }
      return [...prev, clientId];
    });
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      showErrorMsg('Leaderboard name is required');
      return;
    }

    if (!selectedClientIds.length) {
      showErrorMsg('Select at least one client');
      return;
    }

    const entries = selectedClientIds.map((clientId) => ({
      clientId,
      seasonId: selectedSeasonByClient[clientId],
    }));

    if (entries.some((entry) => !entry.seasonId)) {
      showErrorMsg('Choose one season for each selected client');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: name.trim(),
        entries,
      };
      const response = leaderboard
        ? await api.put(`${API_URLS.ADMIN_LEADERBOARDS}/${leaderboard.shareId}`, payload)
        : await api.post(API_URLS.ADMIN_LEADERBOARDS, payload);

      if (!response.data.success) {
        showErrorMsg(response.data.message || 'Failed to save leaderboard');
        return;
      }

      const shareUrl = response.data?.data?.shareUrl || leaderboard?.shareUrl;
      if (!leaderboard && shareUrl) {
        try {
          await navigator.clipboard.writeText(shareUrl);
          showSuccessMsg('Leaderboard created and link copied');
        } catch {
          showSuccessMsg(`Leaderboard created: ${shareUrl}`);
        }
      } else {
        showSuccessMsg(leaderboard ? 'Leaderboard updated' : 'Leaderboard created');
      }

      onSaved();
      onClose();
    } catch (error: any) {
      showErrorMsg(error.response?.data?.message || 'Failed to save leaderboard');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="lg" isLoading={saving}>
      <ModalTitle>{leaderboard ? 'Edit Leaderboard' : 'Create Leaderboard'}</ModalTitle>
      <ModalContent>
        <form id="leaderboard-form" onSubmit={handleSave}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Leaderboard Name</label>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4"
            placeholder="Spring 2026 National Leaderboard"
          />

          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">Clients</label>
            <span className="text-xs text-gray-500">{selectedClientIds.length} selected</span>
          </div>
          <input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3"
            placeholder="Search clients..."
          />
          <div className="border border-gray-200 rounded-md max-h-72 overflow-y-auto divide-y divide-gray-100 mb-5">
            {filteredClients.map((client) => (
              <label key={client._id} className="flex items-start gap-3 p-3 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedClientSet.has(client._id)}
                  onChange={() => toggleClient(client._id)}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">{client.name}</span>
                  <span className="block text-xs text-gray-500">
                    {[client.city, client.state].filter(Boolean).join(', ') || 'No location'}
                  </span>
                </span>
              </label>
            ))}
          </div>

          {selectedClientIds.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">Season per selected client</h3>
              {selectedClientIds.map((clientId) => {
                const client = clients.find((row) => row._id === clientId);
                const options = seasonOptionsByClient[clientId] || [];
                const loading = loadingSeasonByClient[clientId];
                return (
                  <div key={clientId} className="border border-gray-200 rounded-md p-3">
                    <div className="text-sm font-semibold text-gray-900 mb-2">{client?.name || 'Unknown Client'}</div>
                    <select
                      value={selectedSeasonByClient[clientId] || ''}
                      onChange={(event) =>
                        setSelectedSeasonByClient((prev) => ({
                          ...prev,
                          [clientId]: event.target.value,
                        }))
                      }
                      disabled={loading}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">{loading ? 'Loading seasons...' : 'Select one season'}</option>
                      {options.map((season) => (
                        <option key={season._id} value={season._id}>
                          {season.name}
                          {season.active === false ? ' (Inactive)' : ''}
                          {season.startDate && season.endDate ? ` (${season.startDate} to ${season.endDate})` : ''}
                        </option>
                      ))}
                    </select>
                    {!loading && options.length === 0 && (
                      <p className="text-xs text-red-600 mt-2">No seasons found for this client.</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </form>
      </ModalContent>
      <ModalActions>
        <button onClick={onClose} className="bg-gray-500 text-white px-4 py-2 rounded" disabled={saving}>
          Cancel
        </button>
        <button
          form="leaderboard-form"
          type="submit"
          className="bg-green-600 text-white px-4 py-2 rounded"
          disabled={saving}
        >
          {saving ? 'Saving...' : leaderboard ? 'Save Changes' : 'Create'}
        </button>
      </ModalActions>
    </Modal>
  );
};

const Leaderboards: React.FC = () => {
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [leaderboards, setLeaderboards] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLeaderboard, setEditingLeaderboard] = useState<LeaderboardRow | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaderboardToDelete, setLeaderboardToDelete] = useState<LeaderboardRow | null>(null);
  const openCreateModal = () => {
    setEditingLeaderboard(null);
    setIsModalOpen(true);
  };

  const openEditModal = (leaderboard: LeaderboardRow) => {
    setEditingLeaderboard(leaderboard);
    setIsModalOpen(true);
  };


  const fetchData = async () => {
    try {
      setLoading(true);
      const [clientsResponse, leaderboardsResponse] = await Promise.all([
        api.get(API_URLS.ADMIN_CLIENTS),
        api.get(API_URLS.ADMIN_LEADERBOARDS),
      ]);

      if (clientsResponse.data.success) {
        setClients(clientsResponse.data.data || []);
      } else {
        showErrorMsg(clientsResponse.data.message || 'Failed to load clients');
      }

      if (leaderboardsResponse.data.success) {
        setLeaderboards(leaderboardsResponse.data.data || []);
      } else {
        showErrorMsg(leaderboardsResponse.data.message || 'Failed to load leaderboards');
      }
    } catch (error: any) {
      showErrorMsg(error.response?.data?.message || 'Failed to load leaderboards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCopyLink = async (shareUrl: string) => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showSuccessMsg('Link copied to clipboard');
    } catch {
      showErrorMsg('Failed to copy link');
    }
  };

  const openDeleteDialog = (leaderboard: LeaderboardRow) => {
    setLeaderboardToDelete(leaderboard);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!leaderboardToDelete) return;
    try {
      const response = await api.delete(`${API_URLS.ADMIN_LEADERBOARDS}/${leaderboardToDelete.shareId}`);
      if (!response.data.success) {
        showErrorMsg(response.data.message || 'Failed to revoke leaderboard');
        return;
      }
      showSuccessMsg('Leaderboard revoked');
      await fetchData();
    } catch (error: any) {
      showErrorMsg(error.response?.data?.message || 'Failed to revoke leaderboard');
    } finally {
      setDeleteDialogOpen(false);
      setLeaderboardToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-gray-600">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          Loading leaderboards...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <div className="bg-white shadow">
        <div className="px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Leaderboards</h1>
            <p className="text-sm text-gray-600 mt-1">Create shared players leaderboards from selected client seasons.</p>
          </div>
          <button
            onClick={openCreateModal}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Create Leaderboard
          </button>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {leaderboards.length === 0 ? (
            <div className="text-center text-gray-500 p-10">No leaderboards have been created yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accesses</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leaderboards.map((leaderboard) => (
                    <tr key={leaderboard.shareId}>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">{leaderboard.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{leaderboard.accessCount || 0}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {leaderboard.createdAt ? new Date(leaderboard.createdAt).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openEditModal(leaderboard)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-sm"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCopyLink(leaderboard.shareUrl)}
                            className="bg-[#05396b] hover:bg-[#04305a] text-white px-3 py-2 rounded-md text-sm"
                          >
                            Copy Link
                          </button>
                          <button
                            type="button"
                            onClick={() => openDeleteDialog(leaderboard)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md text-sm"
                          >
                            Revoke
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <LeaderboardModal
        isOpen={isModalOpen}
        clients={clients}
        leaderboard={editingLeaderboard}
        onClose={() => setIsModalOpen(false)}
        onSaved={fetchData}
      />

      {deleteDialogOpen && leaderboardToDelete && (
        <ConfirmDialog
          isOpen={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setLeaderboardToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Revoke leaderboard"
          message={`Revoke "${leaderboardToDelete.name}"? The shared link will stop working immediately.`}
          confirmText="Revoke"
          cancelText="Cancel"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />
      )}
    </div>
  );
};

export default Leaderboards;
